// ===========================================
// Roles & Staff Management Page
// ===========================================
// Three tabs: Custom Roles, Staff Members and Admins (support accounts, roles slice 2)

import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Skeleton,
  Tooltip,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import RoleEditorDialog from '../../components/admin/roles/RoleEditorDialog';
import StaffAssignDialog from '../../components/admin/roles/StaffAssignDialog';
import AdminsTab from '../../components/admin/roles/AdminsTab';
import { useOwnerTitle } from '../../hooks/useOwnerTitle';
import { useRoleColorScheme } from '../../hooks/useRoleColorScheme';
import { usePermissions } from '../../hooks/usePermissions';
import {
  useCustomRoles,
  useCustomRole,
  useCreateCustomRole,
  useUpdateCustomRole,
  useDeleteCustomRole,
  useStaffMembers,
  useAssignStaffRole,
  useUpdateStaffRole,
  useRevokeStaffRole,
} from '../../hooks/useCustomRoles';
import {
  CustomRole,
  CustomRoleDetail,
  StaffRoleRecord,
  CreateCustomRoleInput,
  UpdateCustomRoleInput,
} from '../../api/customRole.api';

// ===========================================
// HELPERS
// ===========================================

function RoleColorDot({ color }: { color: string | null }) {
  if (!color) {
    return (
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          bgcolor: '#68707B',
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        bgcolor: color,
      }}
    />
  );
}

/**
 * The login's tier: an Admin is named by the school's owner title (roles slice 2), never "ADMIN".
 * Coloured with the school's User Role Colors (#629; this used its own table, whose Teacher and
 * Parent colours were the avatars' the other way round).
 */
function UserRoleBadge({ role }: { role: string }) {
  const ownerTitle = useOwnerTitle();
  const scheme = useRoleColorScheme();
  const entry = role in scheme ? scheme[role as keyof typeof scheme] : undefined;
  const cfg = entry ? { bgcolor: entry.background, color: entry.text } : { bgcolor: '#68707B', color: '#fff' };
  return (
    <Chip
      label={role === 'ADMIN' ? ownerTitle : role}
      size="small"
      sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: 600, fontSize: '0.7rem' }}
    />
  );
}

function StaffRoleBadge({ name, color }: { name: string; color: string | null }) {
  const bgcolor = color ?? '#68707B';
  return (
    <Chip
      label={name}
      size="small"
      sx={{
        bgcolor,
        // Role colours are free hex: pick black or white text by contrast, so a light colour
        // (brand yellow, mint, cream) stays readable (#399).
        color: (theme) => theme.palette.getContrastText(bgcolor),
        fontWeight: 600,
        fontSize: '0.7rem',
      }}
    />
  );
}

// ===========================================
// CUSTOM ROLES TAB
// ===========================================

interface CustomRolesTabProps {
  onEdit: (role: CustomRole) => void;
  onDelete: (role: CustomRole) => void;
}

function CustomRolesTab({ onEdit, onDelete }: CustomRolesTabProps) {
  const { data: roles, isLoading, isError, error, refetch } = useCustomRoles();

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {(error as Error & { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to load roles. Please try again.'}
      </Alert>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Permissions</TableCell>
              <TableCell align="center">Staff</TableCell>
              <TableCell align="right">
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={() => refetch()}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(6)].map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : !roles || roles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <AdminPanelSettingsIcon
                      sx={{ fontSize: 48, color: 'text.disabled' }}
                    />
                    <Typography color="text.secondary">
                      No custom roles yet. Create one to get started.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              roles.map((role) => (
                <TableRow key={role.id} hover>
                  <TableCell>
                    <RoleColorDot color={role.color} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        {role.name}
                      </Typography>
                      {role.isSystem && (
                        <Chip
                          label="System"
                          size="small"
                          sx={{
                            bgcolor: '#FFCE00',
                            color: '#080808',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {role.description || (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.disabled"
                        >
                          No description
                        </Typography>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={role._count.Permissions}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 40 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={role._count.StaffMembers}
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 40 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Edit role">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(role)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          role.isSystem
                            ? 'System roles cannot be deleted'
                            : role._count.StaffMembers > 0
                            ? 'Remove all staff first'
                            : 'Delete role'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(role)}
                            disabled={
                              role.isSystem || role._count.StaffMembers > 0
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ===========================================
// STAFF MEMBERS TAB
// ===========================================

interface StaffMembersTabProps {
  onEdit: (record: StaffRoleRecord) => void;
  onRevoke: (record: StaffRoleRecord) => void;
}

function StaffMembersTab({ onEdit, onRevoke }: StaffMembersTabProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // One timer across renders (#399): it used to live on the handler function, which is
  // re-created every render, so every keystroke sent its own request.
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(searchTimer.current), []);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(0);
    }, 400);
  };

  const { data, isLoading, isError, error, refetch } = useStaffMembers({
    search: debouncedSearch || undefined,
    page: page + 1,
    limit: rowsPerPage,
  });

  const staffMembers = data?.data ?? [];
  const totalCount = data?.total ?? 0;

  const isExpired = (expiresAt: string | null): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (isError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {(error as Error & { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to load staff members. Please try again.'}
      </Alert>
    );
  }

  return (
    <Paper>
      {/* Search bar */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: 0, sm: 280 }, flex: { xs: 1, sm: '0 0 auto' } }} // #400
          />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Primary Role</TableCell>
              <TableCell>Staff Role</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(6)].map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : staffMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <PersonOffIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography color="text.secondary">
                      {debouncedSearch
                        ? 'No staff members match your search'
                        : 'No staff members assigned yet'}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              staffMembers.map((record) => {
                const expired = isExpired(record.expiresAt);
                return (
                  <TableRow
                    key={record.id}
                    hover
                    sx={{ opacity: expired ? 0.6 : 1 }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {record.User.firstName} {record.User.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {record.User.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <UserRoleBadge role={record.User.role} />
                    </TableCell>
                    <TableCell>
                      <StaffRoleBadge
                        name={record.CustomRole.name}
                        color={record.CustomRole.color}
                      />
                    </TableCell>
                    <TableCell>
                      {record.expiresAt ? (
                        <Typography
                          variant="body2"
                          color={expired ? 'error.main' : 'text.secondary'}
                        >
                          {expired ? 'Expired ' : ''}
                          {format(new Date(record.expiresAt), 'dd/MM/yyyy HH:mm')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          Permanent
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Edit staff role">
                          <IconButton
                            size="small"
                            onClick={() => onEdit(record)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Revoke staff role">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRevoke(record)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Paper>
  );
}

// ===========================================
// MAIN PAGE COMPONENT
// ===========================================

/**
 * Roles are the owner's (slice 1a): the server refuses anyone but an Admin and the route guard hides the
 * page, and this asks nothing of the server for anyone else (a deep link, or a guard that missed it,
 * shows this instead of a row of 403s). Mirrors `RetroEditsReportPage`.
 */
export default function RolesPage() {
  const { isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  if (!isOwner) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Only your {ownerTitle} can manage roles and permissions.</Alert>
      </Box>
    );
  }
  return <RolesPageContent />;
}

function RolesPageContent() {
  const [activeTab, setActiveTab] = useState(0);

  // --- Custom Role state ---
  const [roleEditorOpen, setRoleEditorOpen] = useState(false);
  // Edit mode holds the role's ID; the editor gets the FRESH detail (with permissions) from
  // `useCustomRole` and seeds only once it has fetched (roles plan §5.8, F2).
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  // Bumped on every Edit so the editor never seeds from a cached copy (see useCustomRole).
  const [roleEditorOpenCount, setRoleEditorOpenCount] = useState(0);
  const roleDetail = useCustomRole(editingRoleId ?? '', roleEditorOpenCount, roleEditorOpen);
  const editingRole: CustomRoleDetail | null = editingRoleId ? roleDetail.data ?? null : null;
  const [deleteRoleTarget, setDeleteRoleTarget] = useState<CustomRole | null>(null);

  // --- Staff state ---
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffRoleRecord | null>(null);
  const [revokeStaffTarget, setRevokeStaffTarget] = useState<StaffRoleRecord | null>(null);

  // Data
  const { data: roles } = useCustomRoles();

  // Mutations
  const createRoleMutation = useCreateCustomRole();
  const updateRoleMutation = useUpdateCustomRole();
  const deleteRoleMutation = useDeleteCustomRole();
  const assignStaffMutation = useAssignStaffRole();
  const updateStaffMutation = useUpdateStaffRole();
  const revokeStaffMutation = useRevokeStaffRole();

  // ===========================================
  // ROLE HANDLERS
  // ===========================================

  const handleCreateRole = () => {
    setEditingRoleId(null);
    setRoleEditorOpen(true);
  };

  const handleEditRole = (role: CustomRole) => {
    // The list item has no permissions. Hand the editor the ID; it waits for the fresh
    // detail (it used to open with an empty stub and save a blank role, F2).
    setEditingRoleId(role.id);
    setRoleEditorOpenCount((n) => n + 1);
    setRoleEditorOpen(true);
  };

  const handleDeleteRole = (role: CustomRole) => {
    setDeleteRoleTarget(role);
  };

  const handleRoleSave = async (data: CreateCustomRoleInput | UpdateCustomRoleInput) => {
    // A failure is shown by the hook's onError snackbar; the dialog stays open (#399).
    try {
      if (editingRoleId) {
        await updateRoleMutation.mutateAsync({
          roleId: editingRoleId,
          data,
        });
      } else {
        // In create mode, name and permissions are required by the form
        await createRoleMutation.mutateAsync(data as CreateCustomRoleInput);
      }
    } catch {
      return;
    }
    setRoleEditorOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRoleTarget) return;
    try {
      await deleteRoleMutation.mutateAsync(deleteRoleTarget.id);
    } catch {
      return; // shown by the hook's onError (#399)
    }
    setDeleteRoleTarget(null);
  };

  // ===========================================
  // STAFF HANDLERS
  // ===========================================

  const handleAddStaff = () => {
    setEditingStaff(null);
    setStaffDialogOpen(true);
  };

  const handleEditStaff = (record: StaffRoleRecord) => {
    setEditingStaff(record);
    setStaffDialogOpen(true);
  };

  const handleRevokeStaff = (record: StaffRoleRecord) => {
    setRevokeStaffTarget(record);
  };

  const handleStaffSave = async (data: {
    userId: string;
    customRoleId: string;
    expiresAt?: string | null;
  }) => {
    try {
      if (editingStaff) {
        await updateStaffMutation.mutateAsync({
          userId: editingStaff.userId,
          data: {
            customRoleId: data.customRoleId,
            expiresAt: data.expiresAt,
          },
        });
      } else {
        await assignStaffMutation.mutateAsync(data);
      }
    } catch {
      return; // shown by the hook's onError (#399)
    }
    setStaffDialogOpen(false);
  };

  const handleRevokeConfirm = async () => {
    if (!revokeStaffTarget) return;
    try {
      await revokeStaffMutation.mutateAsync(revokeStaffTarget.userId);
    } catch {
      return; // shown by the hook's onError (#399)
    }
    setRevokeStaffTarget(null);
  };

  const isSavingRole =
    createRoleMutation.isPending || updateRoleMutation.isPending;
  const isSavingStaff =
    assignStaffMutation.isPending || updateStaffMutation.isPending;

  // ===========================================
  // TAB ACTION BUTTON
  // ===========================================

  const tabAction =
    activeTab === 2 ? undefined : activeTab === 0 ? (
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleCreateRole}
      >
        Create Role
      </Button>
    ) : (
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddStaff}
      >
        Add Staff
      </Button>
    );

  // ===========================================
  // RENDER
  // ===========================================

  return (
    <Box>
      <PageHeader
        title="Roles & Staff"
        subtitle="Manage custom permission roles and assign staff members"
        action={tabAction}
      />

      {/* Tab bar */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Custom Roles" />
          <Tab label="Staff Members" />
          <Tab label="Admins" />
        </Tabs>
      </Paper>

      {/* Tab content */}
      {activeTab === 0 && (
        <CustomRolesTab onEdit={handleEditRole} onDelete={handleDeleteRole} />
      )}
      {activeTab === 1 && (
        <StaffMembersTab onEdit={handleEditStaff} onRevoke={handleRevokeStaff} />
      )}
      {activeTab === 2 && <AdminsTab />}

      {/* Role Editor Dialog */}
      <RoleEditorDialog
        open={roleEditorOpen}
        role={editingRole}
        // Not while saving: the save invalidates the detail and the refetch would flash
        // "Loading..." in the closing dialog (the form is already seeded by then).
        isLoading={!!editingRoleId && roleDetail.isFetching && !isSavingRole}
        loadError={!!editingRoleId && !roleDetail.isFetching && !roleDetail.data}
        onClose={() => setRoleEditorOpen(false)}
        onSave={handleRoleSave}
        isSaving={isSavingRole}
      />

      {/* Staff Assign Dialog */}
      <StaffAssignDialog
        open={staffDialogOpen}
        staffRecord={editingStaff}
        roles={roles ?? []}
        onClose={() => setStaffDialogOpen(false)}
        onSave={handleStaffSave}
        isSaving={isSavingStaff}
      />

      {/* Delete Role Confirm */}
      <ConfirmDialog
        open={!!deleteRoleTarget}
        title="Delete Role"
        message={`Are you sure you want to permanently delete the "${deleteRoleTarget?.name}" role? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteRoleMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteRoleTarget(null)}
      />

      {/* Revoke Staff Confirm */}
      <ConfirmDialog
        open={!!revokeStaffTarget}
        title="Revoke Staff Role"
        message={`Remove the staff role from ${revokeStaffTarget?.User.firstName} ${revokeStaffTarget?.User.lastName}? They will lose all staff permissions immediately.`}
        confirmLabel="Revoke"
        confirmColor="error"
        loading={revokeStaffMutation.isPending}
        onConfirm={handleRevokeConfirm}
        onCancel={() => setRevokeStaffTarget(null)}
      />
    </Box>
  );
}
